import { type GroupItemOwnership, type PagingParams, type SortingDir } from '@app/client';

type GroupFiltersCache = { [key: string]: boolean };
type GroupOwnershipCache = { ownership?: GroupItemOwnership[] };
type GroupCollectionsCache = { collections?: string[] };
type GroupFloorPriceCache = { min_floor_price?: string; max_floor_price?: string };

type GroupsCacheKey = Array<
    string | GroupOwnershipCache | GroupFiltersCache | GroupFloorPriceCache | GroupCollectionsCache
>;

interface GroupMember {
    email?: string;
    id: string;
    joined_date: string;
    permission: 'Admin' | 'Member';
    primary_yat?: string;
    status: 'Active' | 'Withdraw' | 'Banned' | 'Removed';
    twitter?: string;
    user_id: string;
}

type GroupItemSorts =
    | 'id'
    | 'user_id'
    | 'yat_page_id'
    | 'purchase_date'
    | 'purchase_price'
    | 'purchase_unit'
    | 'published_at'
    | 'created_at'
    | 'floor_price'
    | 'verified'
    | 'minter';

type GroupItemsFilterQuery = {
    /** Array of collection contract_address */
    contract_addresses?: Array<string>;
    dir?: SortingDir;
    limit?: number;
    /** Array of member id */
    members?: Array<GroupMember['id']>;
    ownership?: GroupItemOwnership[];
    page?: number;
    sort?: GroupItemSorts;
    spam?: boolean;
};

type PageParam = {
    paging: PagingParams;
    data: unknown;
};

// React Query cache keys for groups queries.
export const groupsCacheKeys = {
    groupsKey: ['groups'] as GroupsCacheKey,
    groupAll: (groupId?: string) => (groupId ? (['group', groupId] as const) : (['group'] as const)),
    items: (groupId?: string, filters?: GroupItemsFilterQuery) =>
        ([...groupsCacheKeys.groupAll(groupId), 'items', filters] as const).filter(Boolean),
};

export const usersCacheKeys = {
    inviteData: ['invite-data'] as const,
};

export const getNextPageParam = <T>(lastPage: PageParam & T, pages: (PageParam & T)[]) => {
    if (lastPage && lastPage?.paging?.total && lastPage?.paging?.limit && lastPage?.paging?.page != null) {
        const total = lastPage?.paging?.total || 0;
        const limit = lastPage?.paging?.limit || 1;
        const totalPages = Math.ceil(total / limit);

        if (pages?.length < totalPages) {
            return pages?.length;
        } else {
            return undefined;
        }
    }
    return undefined;
};

export const getPagesContent = <T>(pages?: (PageParam & T)[]) =>
    (pages?.map((page) => page?.data).flat() ?? []) as unknown as (PageParam & T)['data'];

export const getPagesPagingParams = <T>(pages?: (PageParam & T)[]) => {
    const paging = pages?.map((page) => page?.paging)?.flat();
    return paging?.[paging?.length - 1 || 0] || paging?.[0] || ({ page: 0, limit: 20, total: 0 } as PagingParams);
};
